import { forwardRef } from "react";
import { Globe } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useLanguage } from "@/providers/LanguageProvider";
import { useTranslation } from "react-i18next";
import { stripLocaleFromPath } from "@/i18n/locale-utils";
import type { SupportedLanguage } from "@/i18n";
import { toast } from "sonner";

export const LanguageSwitcher = forwardRef<HTMLButtonElement>((_props, ref) => {
  const { language, setLanguage, supported, labels } = useLanguage();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const handleSwitch = (lng: SupportedLanguage) => {
    setLanguage(lng);
    const { rest } = stripLocaleFromPath(location.pathname);
    const target = `/${lng}${rest === "/" ? "" : rest}${location.search}${location.hash}` || `/${lng}`;
    navigate(target, { replace: true });
    toast.success(t("common.languageSwitched", { label: labels[lng] }));
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button ref={ref} variant="ghost" size="sm" aria-label={t("common.language")} className="gap-2">
          <Globe className="h-4 w-4" />
          <span className="hidden sm:inline uppercase">{language}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {supported.map((lng) => (
          <DropdownMenuItem
            key={lng}
            onClick={() => handleSwitch(lng)}
            className={language === lng ? "bg-accent/40" : ""}
          >
            <span className="mr-2 uppercase text-xs text-muted-foreground">{lng}</span>
            {labels[lng]}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
});
LanguageSwitcher.displayName = "LanguageSwitcher";
